import { STEP_LABELS } from '../../../pages/integrations/measurements/setup/copy.js'

/**
 * "Set up Rock" (the measurements board). One anchor per rail step, seeded
 * from the rail's own labels, plus the actions inside the steps. The steps
 * rail is desktop-only; on a phone `rock_setup.step_picker` stands in for it.
 */

const ROUTE = '/settings/integrations/rock/:id/measurements'
const ENTITY = 'rock_connection'
/** Rock connections have no object-level sharing: the admin role gate is the whole rule. */
const PERMISSION = 'view'

const STEP_INTENTS = {
  connect: 'Check the Rock connection and reconnect or allow creating metrics',
  match: 'Match Rock campuses and service times to Graphit',
  choose: 'Choose which Rock measurements to bring into Graphit',
  activity: 'Watch Graphit set up the chosen measurements',
  first_values: 'Check the first values and build a dashboard from them',
}

const seededStepAnchors = Object.entries(STEP_LABELS).map(([key, label]) => ({
  id: `rock_setup.step.${key}`,
  label,
  intent: STEP_INTENTS[key] || label,
  route: ROUTE,
  minRole: 'admin',
  entity: ENTITY,
  permission: PERMISSION,
  seededFrom: 'rockSetupSteps',
}))

const step = (id, label, intent, extra = {}) => ({ id, label, intent, route: ROUTE, minRole: 'admin', entity: ENTITY, permission: PERMISSION, ...extra })

/** The first-run setup controls: "Match all", "Configure manually" and the bulk checklist. */
const FIRST_RUN_ANCHORS = [
  step('integrations.setup.match_all_campuses', 'Match all campuses', 'Match every Rock campus to Graphit by name in one go, adding any that are missing', { parent: 'rock_setup.step.match' }),
  step('integrations.setup.configure_manually_campuses', 'Configure manually', 'Match Rock campuses to Graphit campuses one by one', { parent: 'rock_setup.step.match' }),
  step('integrations.setup.match_all_service_times', 'Match all service times', 'Match every Rock service time to Graphit in one go, adding any that are missing', { parent: 'rock_setup.step.match' }),
  step('integrations.setup.configure_manually_service_times', 'Configure manually', 'Match Rock service times to Graphit services one by one', { parent: 'rock_setup.step.match' }),
  step('integrations.setup.checklist', 'Metrics checklist', 'Pick the Rock measurements and metrics to bring into Graphit', { parent: 'rock_setup.step.choose' }),
  step('integrations.setup.select_all', 'Select all', 'Tick every measurement in the checklist', { parent: 'integrations.setup.checklist' }),
  step('integrations.setup.add_selected', 'Add selected', 'Set up every ticked measurement at once', { parent: 'integrations.setup.checklist' }),
  step('integrations.setup.retry_failed', 'Retry the ones that didn’t work', 'Try again for the measurements that failed to set up', { parent: 'integrations.setup.checklist' }),
  step('integrations.setup.answer_questions', 'Answer questions', 'Answer a measurement’s questions (or edit its choices) before adding it', { parent: 'integrations.setup.checklist' }),
  step('integrations.setup.create_in_rock_instead', 'Create a new one in Rock instead', 'Create a fresh Rock metric instead of using the one Graphit found', { parent: 'integrations.setup.answer_questions' }),
]

export const ROCK_SETUP_ANCHORS = [
  ...seededStepAnchors,
  ...FIRST_RUN_ANCHORS,
  step('rock_setup.step_picker', 'Setup step', 'Jump to a setup step (phone)'),
  step('rock_setup.next', 'Next step', 'Go to the next setup step that needs you'),
  step('rock_setup.ask_assistant', 'Get help with this step', 'Ask Graphit for help with the Rock setup step you are on'),
  step('rock_setup.more_matches', 'More matches (optional)', 'Match other Rock values such as groups and categories', { parent: 'rock_setup.step.match' }),
  step('rock_setup.view_progress', 'View progress', 'See what Graphit is setting up right now', { parent: 'rock_setup.step.activity' }),
]
